import _ from 'lodash';
import Alpine from 'alpinejs';
import fit from '../lib/alpine-fit.js';
import focus from '@alpinejs/focus';
import Parent from '@ryangjchandler/alpine-parent';
import round from 'round-half-even';
import { v4 as uuidv4 } from 'uuid';

import '../lib/modernizr.js';
import '../src/utils.js';
import './src/constants.js';


// We make use of the fact that on load this file is the "last" in the DOM
const scripts = document.getElementsByTagName('script');
const script = scripts[scripts.length - 1];

global.config = {
    csrfToken: script.getAttribute('data-csrf-token'),
    instance: script.getAttribute('data-instance'),
    language: script.getAttribute('data-language'),
};

function setViewportHeight() {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
}

window.addEventListener('resize', setViewportHeight);
setViewportHeight();

document.addEventListener('DOMContentLoaded', function() {
    // Detect modern feature set
    let href = window.location.href.toLowerCase();
    let href_is_catalogue = href.indexOf('//catalog.') !== -1;
    let href_is_unsupported = href.indexOf('/unsupported') !== -1;

    if (href_is_catalogue && !href_is_unsupported) {
        // Ensure support for native JSON parsing and the Fetch API
        if (!Modernizr.json || !Modernizr.fetch) {
            window.location.href = `/${global.dataInstance}/unsupported`;
        }
    }

    if (href_is_catalogue && href_is_unsupported) {
        // Customers often end up on this view even after installing or
        // updating to a modern browser, so redirect them back
        if (Modernizr.json && Modernizr.fetch) {
            window.location.href = `/${global.dataInstance}/index`;
        }
    }

    // Set up Alpine et al
    Alpine.plugin(fit);
    Alpine.plugin(focus);
    Alpine.plugin(Parent);

    window.Alpine = Alpine;
    Alpine.start();

    // Fix keyboard zoom on iOS
    // https://weblog.west-wind.com/posts/2023/Apr/17/Preventing-iOS-Safari-Textbox-Zooming
    if (navigator.userAgent.indexOf('iPhone') > -1) document.querySelector('[name=viewport]').setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1');
});


global.Helpers = {
    isInput: function(e) {
        if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return true;
        return false;
    },
};

global.Components = {}

global.Components.v3 = {
    /*
     * A base set of methods that can be inherited by other components
     *
     */
    Base: function() {
        return {
            setProps: function(props, applyToProperty) {
                // Sets all passed props in the component namespace
                let target = applyToProperty ? this[applyToProperty] : this;

                for (let prop in props) {
                    if (props[prop]._x_dataStack) {
                        // Store the Alpine component Proxy, not the DOM element
                        target[prop] = props[prop]._x_dataStack[0].$data;
                    } else {
                        target[prop] = props[prop];

                        // If prop is a $ref and is a form element, try to set it
                        if (target.$refs[prop] && target.$refs[prop].type == 'checkbox') {
                            if (props[prop] === true || props[prop] === 'true') {
                                target.$refs[prop].checked = true;
                            } else {
                                target.$refs[prop].checked = false;
                            }
                        }
                    }
                }
                return this;
            },
            openComponent: function(elemOrId, innerElemOrId, props, applyToProperty) {
                // Find and open a target component
                let component = $component(elemOrId);
                // Optionally setting props on the target, or a nested target
                let innerComponent = innerElemOrId ? $component(innerElemOrId) : component;

                if (props) {
                    if (innerComponent.willSetProps) innerComponent.willSetProps(applyToProperty);
                    innerComponent.setProps(props, applyToProperty);
                    if (innerComponent.didSetProps) innerComponent.didSetProps(applyToProperty);
                }
                component.open();

                if (typeof this.close === 'function') {
                    // Optionally close the source (trigger) component
                    this.close();
                }
            },
        }
    },
    /*
     * A Button implementation with state
     *
     * This is a supercharged button which displays a busy/loading state
     * whilst doing work™. The button itself is disabled during submit, and
     * can optionally show success or failure states, or simply redirect.
     *
     */
    Button: function() {
        return {
            _loading: false,
            _status: null,

            redirectTimeout: 500,
            statusTimeout: 2000,

            invoke: function(func, urlOnSuccess, urlOnFailure) {
                // TODO: integrate HTML5 form validation methods before "submitting"

                // Don't disable this using a :disabled attribute otherwise
                // state updates first and a standard form will never submit
                this.$root.setAttribute('disabled', true);
                this._loading = true;

                if (func) {
                    func()
                    .then(() => {
                        if (urlOnSuccess) {
                            // Show success state (but don't enable button)
                            // while the client is redirecting
                            this.setStatus(true, false);
                            setTimeout(() => {
                                window.location.href = urlOnSuccess;
                            }, this.redirectTimeout);
                        } else {
                            this.setStatus(true);
                        }
                    })
                    .catch(e => {
                        if (urlOnFailure) {
                            this.setStatus(false, false);
                            setTimeout(() => {
                                window.location.href = urlOnFailure;
                            }, this.redirectTimeout);
                        } else {
                            this.setStatus(false);
                            console.error(e);
                        }
                    });
                } else {
                    // By not specifying `func` you can allow the form to
                    // submit normally, while showing a loading indicator
                    setTimeout(() => {
                        // Trigger submit explicitly as browsers don't
                        // bubble up the click event consistently
                        this.$root.closest('form').submit();
                    }, this.redirectTimeout);
                }
            },
            isValid: function($parent) {
                // Usually we want to defer to Form.isValid(),
                // passed via Alpine's $parent magic
                if ($parent && typeof $parent.isValid === 'function') return $parent.isValid();

                // …but we also allow Buttons to stand alone without
                // a Form if no validation is required
                return true;
            },
            reset: function() {
                this._loading = false;
                this._status = null;
                this.$root.removeAttribute('disabled');
            },
            setStatus: function(status, reset = true) {
                this._status = (status === true);

                if (reset) {
                    setTimeout(() => {
                        this.reset();
                    }, this.statusTimeout);
                }
            },
            isLoading: function() {
                return this._loading;
            },
            isPending: function() {
                return this._status !== true && this._status !== false;
            },
            isSuccess: function() {
                return this._status === true;
            },
            isFailure: function() {
                return this._status === false;
            },
        }
    },
    /*
     * A basic dropdown component
     *
     */
    Dropdown: function(openOnInit) {
        return {
            _isOpen: (openOnInit === true),

            open: function() {
                this._isOpen = true;
            },
            close: function() {
                this._isOpen = false;
            },
            toggle: function() {
                this._isOpen = !this._isOpen;
            },
            isOpen: function() {
                return this._isOpen;
            },
        }
    },
    /*
     * A filter selection implementation, allowing for the selection,
     * validation and application of variable data key-value types
     *
     * This is the parent component, enclosing multiple Filter children
     *
     */
    Filters: function() {
        return {
            count: 0,
            filters: [],

            _isOpen: false,
            _childrenDidInit: false,

            init: function() {
                this.$watch('_childrenDidInit', _ => {
                    this.filters.forEach(component => {
                        if (component.isEnabled()) this.count++;
                    });
                });
            },
            apply: function() {
                let urlParams = new URLSearchParams(window.location.search);

                this.filters.forEach(component => {
                    urlParams = component.process(urlParams);
                });

                // Reset any pagination param
                urlParams.delete('page');

                // Include any free-form search string in the filter
                let q = this.$root.closest('form').querySelector('input[name="q"]');
                if (q && q.value) {
                    urlParams.set('q', q.value);
                } else {
                    urlParams.delete('q');
                }

                window.location.search = urlParams;
            },
            open: function() {
                this._isOpen = true;
            },
            close: function() {
                this._isOpen = false;
            },
            toggle: function() {
                this._isOpen = !this._isOpen;
            },
            isOpen: function() {
                return this._isOpen;
            },
            hasActive: function() {
                return this.count > 0;
            },
        }
    },
    /*
     * A filter selection implementation, allowing for the selection,
     * validation and application of variable data key-value types
     *
     * This is the child component, should be enclosed by a Filters parent
     *
     */
    Filter: function(name, widget, isEnabled, allowMultiple, keys, operator) {
        return {
            filterGroup: undefined,

            name: name,
            widget: widget || 'select',
            // Implied for checkbox, radio, date, optional for select
            allowMultiple: (allowMultiple === true && (widget || 'select') == 'select'),

            _isEnabled: (isEnabled === true),
            // Used to get scope of allowed param keys if widget == date
            __keys: keys || [],
            // Used for x-model if widget == date
            __operator: operator || '__gte,__lte',

            // No point to auto-evaluate init() without required arguments,
            // so must be called explicitly with x-init
            init_: function(filterGroup) {
                this.$nextTick(() => {
                    this.filterGroup = filterGroup;
                    this.filterGroup.filters.push(this);
                });
            },
            process: function(urlParams) {
                if (this.widget == 'hidden') {
                    let elem = this.$root.querySelector('.filter-values input[type="hidden"]');

                    if (this.isEnabled() && elem && elem.value) {
                        urlParams.set(this.name, elem.value);
                    } else {
                        urlParams.delete(this.name);
                    }
                }
                else if (this.widget == 'checkbox') {
                    // Implies allowMultiple: true, remove existing values first
                    urlParams.delete(this.name);

                    if (this.isEnabled()) {
                        this.$root.querySelectorAll('.filter-values input[type="checkbox"]:checked').forEach(elem => {
                            urlParams.append(this.name, elem.value);
                        });
                    }
                }
                else if (this.widget == 'radio') {
                    // Implies allowMultiple: false
                    let elem = this.$root.querySelector('.filter-values input[type="radio"]:checked');
                    if (this.isEnabled() && elem && elem.value) {
                        urlParams.set(this.name, elem.value);
                    } else {
                        urlParams.delete(this.name);
                    }
                }
                else if (this.widget == 'select') {
                    // TODO: check for allowMultiple
                    let elem = this.$root.querySelector('.filter-values select option:checked');
                    if (this.isEnabled() && elem && elem.value) {
                        urlParams.set(this.name, elem.value);
                    } else {
                        urlParams.delete(this.name);
                    }
                }
                else if (this.widget == 'date') {
                    urlParams.forEach((value, param) => {
                        this.__keys.forEach(key => {
                            if (param.startsWith(key)) urlParams.delete(param);
                        });
                    });

                    if (this.isEnabled()) {
                        let operand_elem = this.$root.querySelector('.filter-values [name$=".__operand"]');
                        let operator_elem = this.$root.querySelector('.filter-values [name$=".__operator"]');
                        let from_elem = this.$root.querySelector('.filter-values [name$=".from"]');
                        let to_elem = this.$root.querySelector('.filter-values [name$=".to"]');

                        let requiresRange = !(operator_elem.value.indexOf(',') == -1);

                        if (
                            operand_elem.value &&
                            operator_elem.value &&
                            from_elem.value &&
                            (!requiresRange || to_elem.value)
                        ) {
                            let values = [];

                            values.push(DateTime.fromSQL(from_elem.value));
                            let isValid = values[0].isValid;

                            if (isValid && requiresRange) {
                                values.push(DateTime.fromSQL(to_elem.value));
                                isValid = values[1].isValid;
                            }

                            if (isValid) {
                                operator_elem.value.split(',').forEach((operator, idx) => {
                                    urlParams.set(`${operand_elem.value}${operator}`, values[idx].toISODate());
                                });
                            }
                        }
                    }
                }

                return urlParams;
            },
            set: function(values) {
                // Set a filter value from the frontend (ie pre-submit),
                // values should be an object of key: value pairs for the
                // required filter input elements
                Object.entries(values).forEach(([key, value]) => {
                    const input = this.$root.querySelector(`[name="filters.${this.name}.${key}"]`);
                    if (input) input.value = value;
                });
            },
            enable: function() {
                this._isEnabled = true;
            },
            disable: function() {
                this._isEnabled = false;
            },
            toggle: function() {
                this._isEnabled = !this._isEnabled;
            },
            setEnabled: function(value) {
                this._isEnabled = (value == true);
            },
            isEnabled: function() {
                return this._isEnabled;
            },
        }
    },
    /*
     *
     */
    Menu: function(toggleElemId, isFullHeight) {
        return {
            _open: false,
            _toggleElemId: toggleElemId,
            _isFullHeight: (isFullHeight === true),

            open: function() {
                this._open = true;

                if (this._isFullHeight) {
                    let body = document.querySelector('body');
                    body.dataset.overflowSetBy = this._toggleElemId;
                    body.classList.add('overflow-hidden');
                }
            },
            close: function() {
                this._open = false;

                if (this._isFullHeight) {
                    let body = document.querySelector('body');

                    if (body.dataset.overflowSetBy == this._toggleElemId) {
                        delete body.dataset.overflowSetBy;
                        body.classList.remove('overflow-hidden');
                    }
                }
            },
            toggle: function() {
                if (this._open) {
                    this.close();
                } else {
                    this.open();
                }
            },
            isOpen: function() {
                return this._open;
            },
        }
    },
    /*
     * A basic implementation of Thing That Opens™
     *
     */
    Modal: function(openOnInit, locationHash) {
        return {
            _open: (openOnInit === true || locationHash && window.location.hash == '#' + locationHash),
            locationHash: locationHash,

            open: function() {
                this._open = true;
                if (this.locationHash) window.location.hash = '#' + this.locationHash;
            },
            close: function() {
                this._open = false;
                if (this.locationHash) window.location.hash = '';
            },
            toggle: function() {
                this._open = !this._open;
                if (this.locationHash) window.location.hash = (this._open ? '#' + this.locationHash : '');
            },
            isOpen: function() {
                return this._open;
            },
        }
    },
    /*
     * A numeric input with ± "spinner" controls to allow the user to
     * quickly increment or decrement the value, with optional bounding
     * constraints (min, max) on the value
     *
     */
    NumberSpinner: function(minQuantity, maxQuantity, config) {
        return {
            value: config?.alwaysIncludeZero ? 0 : (minQuantity || 0),
            // If the input is empty, it will be replaced with this value
            missingValue: config?.missingValue || 0,

            useSelect: (config?.useSelect === true),
            // Includes a zero option even when MIN_VALUE is greater
            alwaysIncludeZero: !minQuantity || (config?.alwaysIncludeZero === true),
            // Special values are indexed with negative numbers, and when
            // enabled, will disable incremental steps below zero
            specialValues: config?.specialValues || [],
            selectValues: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20],

            MIN_SELECT_VALUE: config?.minSelectValue || 0,
            MAX_SELECT_VALUE: config?.maxSelectValue || 20,
            SELECT_STEP_VALUE: config?.selectStepValue || 1,

            MIN_VALUE: minQuantity || 0,
            MAX_VALUE: (maxQuantity === null ? 10000 : maxQuantity),

            init: function() {
                this.useSpecialValues = (!!this.specialValues.length);

                this.$nextTick(() => {
                    this.refreshSelect();
                    this.$watch('MIN_VALUE', _ => this.refreshSelect());
                    this.$watch('MAX_VALUE', _ => this.refreshSelect());

                    this.updateState(this.value);
                });
            },
            isMinValue: function() {
                return this.value <= (this.alwaysIncludeZero ? 0 : this.MIN_VALUE);
            },
            isMaxValue: function() {
                return this.value >= this.MAX_VALUE;
            },
            refreshSelect: function() {
                const min = Math.max(this.MIN_SELECT_VALUE, Math.ceil(this.MIN_VALUE), 0); // Prevent fractional select values
                const max = Math.min(this.MAX_SELECT_VALUE, this.MAX_VALUE);

                const selectValues = Array.from({
                    length: (max - min) / this.SELECT_STEP_VALUE + 1
                }, (value, idx) => min + idx * this.SELECT_STEP_VALUE);

                if (min > 0 && this.alwaysIncludeZero) selectValues.unshift(0);

                this.selectValues = selectValues;
            },
            setValue: function(value) {
                this.updateState(value);
            },
            update: function(e) {
                const inputElem = this.$refs.input;

                if (this.useSelect) {
                    const selectElem = this.$refs.select;

                    if (e.target == selectElem) {
                        // Push dropdown value to input value, but only
                        // if the event originated from the select itself
                        if (selectElem.value == 'other') {
                            selectElem.style.display = 'none';
                            inputElem.style.display = 'block';
                            inputElem.focus();
                            return;
                        }
                        inputElem.value = selectElem.value;
                    }
                }

                this.updateState(inputElem.value);
            },
            updateState: function(quantity, minQuantity, maxQuantity) {
                if (minQuantity !== undefined) this.MIN_VALUE = minQuantity || 0;
                if (maxQuantity !== undefined) this.MAX_VALUE = (maxQuantity === null ? 10000 : maxQuantity);

                if (quantity === undefined || quantity === null) return;

                const previousValue = this.value;

                let value = 0;
                let values = [];

                if (this.specialValues.includes(parseInt(quantity))) {
                    this.value = parseInt(quantity);
                } else {
                    try {
                        quantity = String(quantity);

                        // Provide mini-calculator functionality within the input field
                        if (quantity.indexOf('/') !== -1) {
                            values = quantity.split('/');
                            value = round(parseFloat(values[0], 10) / parseFloat(values[1] , 10), 2);
                        } else if (quantity.indexOf('*') !== -1) {
                            values = quantity.split('*');
                            value = round(parseFloat(values[0], 10) * parseFloat(values[1] , 10), 2);
                        } else if (quantity.indexOf('+') !== -1) {
                            values = quantity.split('+');
                            value = round(parseFloat(values[0], 10) + parseFloat(values[1] , 10), 2);
                        } else if (quantity.indexOf('-') !== -1) {
                            values = quantity.split('-');
                            value = round(parseFloat(values[0], 10) - parseFloat(values[1] , 10), 2);
                        } else {
                            value = parseFloat(quantity);
                        }
                    } catch(e) {
                        console.warn(e);
                        value = 0;
                    }

                    if (!isNumeric(value)) {
                        value = this.missingValue;
                    } else {
                        value = Math.abs(value);
                    }

                    if (value == 0 && this.alwaysIncludeZero) {
                        // No need to round/enforce MIN_VALUE if zero allowed
                        this.value = 0;
                    } else {
                        this.value = Math.max(
                            Math.min(value, this.MAX_VALUE),
                            this.MIN_VALUE,
                        );

                        if (this.value == this.MIN_VALUE && value < this.MIN_VALUE) this.$dispatch('value-min');
                        if (this.value == this.MAX_VALUE && value > this.MAX_VALUE) this.$dispatch('value-max');
                    }
                }

                const inputElem = this.$refs.input;
                if (inputElem) inputElem.value = this.value;

                if (this.useSelect) {
                    const selectElem = this.$refs.select;

                    if (inputElem && selectElem) {
                        if (
                            this.specialValues.includes(this.value) ||
                            this.selectValues.indexOf(this.value) != -1
                        ) {
                            selectElem.value = this.value;

                            inputElem.style.display = 'none';
                            selectElem.style.display = 'block';
                        } else {
                            selectElem.style.display = 'none';
                            inputElem.style.display = 'block';
                        }
                    }
                }

                if (this.value != previousValue) this.$dispatch('value-changed');
            },
            updateValue: function(byValue) {
                const isDecrease = (byValue && byValue < 0);
                const effectiveValue = Math.round(Math.floor(this.value) + parseFloat(byValue));

                if (isDecrease && effectiveValue < this.MIN_VALUE && this.alwaysIncludeZero) {
                    this.updateState(0);
                } else {
                    this.updateState(effectiveValue);
                }
            },
        }
    },
    /*
     * A basic single-task queuing implementation that accepts tasks that
     * can be fetch()ed; add tasks with push({resource: …, options: {…}})
     *
     */
    Queue: function() {
        return {
            _checkInterval: 100,  // In ms
            _checkTimeout: 30000, // In ms
            _isPendingResult: false,

            queue: [],
            results: {},

            init: function() {
                // Watching `queue` *and* `_isPendingResult` ensures
                // `process()` is called when the queue changes in length, or
                // a task completes which, coupled with the check for
                // `_isPendingResult` means only a single task runs at once
                this.$watch('queue', _ => this.process());
                this.$watch('_isPendingResult', _ => this.process());
            },
            awaitResult: function(uuid) {
                const start = new Date();

                // Returns a Promise that behaves like await in waiting for
                // a result to populate for the task with id `uuid`
                return new Promise((resolve, reject) => {
                    (function checkForResult(uuid) {
                        if (this.results[uuid]) {
                            const result = this.results[uuid];
                            delete this.results[uuid];

                            return resolve(result);
                        }

                        if ((new Date()) - start > this._checkTimeout) {
                            // Avoid infinite polling for a result if something
                            // goes wrong outside of the Promise resolve/reject
                            // callback (note this does not guarantee the task
                            // will never complete, just that its result will
                            // not be returned as a success)
                            return reject('Timeout');
                        }

                        setTimeout(checkForResult.bind(this, uuid), this._checkInterval);
                    }).bind(this, uuid)();
                });
            },
            process: async function(length) {
                if (this._isPendingResult) return;

                const task = this.queue.shift();

                if (task) {
                    this._isPendingResult = true;

                    const result = await fetch(task.resource, task.options);
                    this.results[task.id] = result;

                    this._isPendingResult = false;
                }
            },
            push: function(fetchable) {
                if (!fetchable || _.isEmpty(fetchable)) return;

                const uuid = uuidv4();
                fetchable.id = uuid;

                this.queue.push(fetchable);

                return this.awaitResult(uuid);
            },
        }
    },
};

global.Boost = {
    Basket: function(
        orderId,
        subtotal,
        subtotalAfterTax,
        fee,
        fees,
        discount,
        discounts,
        tax,
        taxes,
        total,
        includesSavingsOf,
    ) {
        return {
            catalogue: undefined,
            queue: undefined,

            orderId: String(orderId),
            subtotal: subtotal || 0,
            subtotalAfterTax: subtotalAfterTax || 0,
            fee: fee || 0,
            fees: fees || [],
            discount: discount || 0,
            discounts: discounts || [],
            tax: tax || 0,
            taxes: taxes || [],
            total: total || 0,
            includesSavingsOf: includesSavingsOf || 0,

            _childrenDidInit: false,
            _hasError: false,
            _isPendingUpdate: false,

            // Items should push into this array on init()
            items: [],

            // No point to auto-evaluate init() without required arguments,
            // so must be called explicitly with x-init
            init_: function(catalogue, queue) {
                this.$nextTick(() => {
                    this.catalogue = catalogue;
                    this.catalogue.basket = this;

                    this.queue = queue;

                    this.$watch('_childrenDidInit', _ => this.process());
                });
            },
            hasItem: function(skuId) {
                return this.items.findIndex(item => item.skuId == skuId);
            },
            hasItems: function(withQuantity) {
                if (withQuantity) {
                    let hasItems = false;

                    for (const item of this.items) {
                        if (item.selectedQuantity) {
                            hasItems = true;
                            break;
                        }
                    }

                    return hasItems;
                }

                return this.items && this.items.length > 0;
            },
            hasError: function() {
                return this._hasError;
            },
            hasSavings: function() {
                return this.includesSavingsOf > 0;
            },
            hydrate: async function(data, triggeredByCatalogue) {
                if (_.isEmpty(data)) return;

                // By definition, the Basket has *all* BasketItems, not
                // just those CatalogueItems in the current view, so any
                // "unknown" items in data.lines need to be added
                const skuIds = new Set();

                data.lines.forEach(line => {
                    const idx = this.hasItem(line.sku_id);

                    if (idx === -1) {
                        // Basket is missing line item
                        let rv = this.queue.push({
                            resource: new URL(`/${config.instance}/orders/basket-item.json`, window.location.href),
                            options: {
                                method: 'POST',
                                mode: 'same-origin',
                                credentials: 'same-origin',
                                body: JSON.stringify({
                                    order_id: this.orderId,
                                    sku_id: line.sku_id,
                                }),
                                headers: {
                                    'Content-Type': 'application/json',
                                    'Accept': 'application/json',
                                    'X-CSRFToken': config.csrfToken,
                                },
                            }
                        })
                        .then(r => {
                            if (!r.ok) throw new Error('Invalid response', {response: r});
                            return r.text();
                        })
                        .then(html => {
                            let parser = new DOMParser();
                            // Note parseFromString returns a full document
                            let elem = parser.parseFromString(html, 'text/html').body.firstChild;
                            $('basket-items').appendChild(elem);

                            this.$dispatch('item-added', {skuId: line.sku_id});
                        })
                        .catch(e => {
                            console.warn(e);
                            this.toggleErrorState();
                        });
                    } else {
                        // Basket has line item
                        let isPendingUpdate = false;
                        if (triggeredByCatalogue) isPendingUpdate = this.items[idx].setIsPendingUpdate(true);

                        this.items[idx].selectedQuantity = line.quantity;
                        this.items[idx].amount = line.amount;
                        this.items[idx].labels = line.labels.filter(label => label.type == 'offer');

                        if (triggeredByCatalogue) this.items[idx].setIsPendingUpdate(isPendingUpdate);
                    }

                    skuIds.add(line.sku_id);
                });

                this.items.forEach(item => {
                    // If an item is reduced to zero, it isn't returned in
                    // data.lines (since the order line has been deleted)
                    if (!skuIds.has(item.skuId)) {
                        if (triggeredByCatalogue) {
                            // If the item was reduced from the controls in
                            // the *Catalogue*, we can just remove the
                            // BasketItem entirely
                            this.removeItem(item.skuId);
                        } else {
                            // If the item was reduced from the controls in
                            // the *Basket*, we leave the BasketItem in the UI
                            // in case the user made a mistake and wants to
                            // easily revert to a non-zero quantity (since it
                            // could be non-trivial to find that item again as
                            // it may not be in the current view), but if it
                            // *is* in the view, we also need to tell
                            // CatalogueItem to set quantity = 0
                            $component(`catalogue-item-${item.baseSkuId}`)?.reset(item.skuId, true);
                        }
                    }
                });

                this.subtotal = data.subtotal;
                this.subtotalAfterTax = data.subtotal_after_tax;
                this.fee = data.fee;
                this.fees = data.fees;
                this.discount = data.discount;
                this.discounts = data.discounts;
                this.tax = data.tax;
                this.taxes = data.taxes;
                this.total = data.total;
                this.includesSavingsOf = data.includes_savings_of;

                // Remove any location hash associated with the open basket
                // if no items remain (since on refresh the basket will have
                // no visible DOM, causing the user to get trapped)
                if (!triggeredByCatalogue && !this.hasItems(true)) window.location.hash = '';
            },
            isPendingUpdate: function() {
                return this._isPendingUpdate;
            },
            process: function() {

            },
            removeItem: function(skuId) {
                let idx = this.items.findIndex(item => item.skuId == skuId);

                if (idx !== -1) {
                    this.items[idx].$root.remove();
                    this.items.splice(idx, 1);
                    this.$dispatch('item-removed', {skuId: skuId});
                }
            },
            submit: function() {
                this._isPendingUpdate = true;
            },
            toggleErrorState: function(timeout) {
                this._hasError = true;
                setTimeout(() => { this._hasError = false }, timeout || 5000);
            },
        }
    },
    BasketItem: function(
        skuId,
        baseSkuId,
        selectedQuantity,
        amount,
        labels,
    ) {
        return {
            basket: undefined,

            skuId: String(skuId),
            baseSkuId: String(baseSkuId),
            selectedQuantity: selectedQuantity || 0,
            amount: amount || 0,
            labels: (labels || []).filter(label => label.type == 'offer'),

            _hasFlash: false,
            _isPendingUpdate: false,

            // No point to auto-evaluate init() without required arguments,
            // so must be called explicitly with x-init
            init_: function(basket) {
                this.$nextTick(() => {
                    this.basket = basket;
                    this.basket.items.push(this);

                    this.$watch('selectedQuantity', value => {
                        // Block concurrency/race conditions
                        if (!this._isPendingUpdate) this.update(value);
                    });
                    this.$dispatch('item-inited');
                });
            },
            hasFlash: function() {
                return this._hasFlash;
            },
            setFlash: function(text, classes, timeout=1000) {
                if (!this.$refs.flash) return;

                this.$refs.flash.innerHTML = text;
                this.$refs.flash.classList.add(classes);
                this._hasFlash = true;

                setTimeout(() => {
                    this._hasFlash = false;
                    this.$refs.flash.classList.remove(classes);
                }, timeout);
            },
            isPendingUpdate: function() {
                return this._isPendingUpdate;
            },
            setIsPendingUpdate: function(value) {
                // Returns the current value, which can be restored later
                const isPendingUpdate = this._isPendingUpdate;
                this._isPendingUpdate = (value === true);
                return isPendingUpdate;
            },
            update: function(quantity) {
                this._isPendingUpdate = true;
                this.basket._isPendingUpdate = true;

                let rv = this.basket.queue.push({
                    resource: new URL(`/${config.instance}/orders/${this.basket.orderId}/update`, window.location.href),
                    options: {
                        method: 'PATCH',
                        mode: 'same-origin',
                        credentials: 'same-origin',
                        body: JSON.stringify({
                            lines: [{
                                sku_id: this.skuId,
                                quantity: quantity,
                            }],
                        }),
                        headers: {
                            'Content-Type': 'application/json',
                            'Accept': 'application/json',
                            'X-CSRFToken': config.csrfToken,
                        },
                    }
                })
                .then(r => {
                    if (!r.ok) throw new Error('Invalid response', {response: r});
                    return r.json();
                })
                .then(data => {
                    this.basket.hydrate(data)
                    .then(() => this.basket.catalogue.hydrate(data, true))
                    .then(() => {
                        this._isPendingUpdate = false;
                        this.basket._isPendingUpdate = false;
                    });
                })
                .catch(e => {
                    console.warn(e);
                    this.basket.toggleErrorState();

                    this._isPendingUpdate = false;
                    this.basket._isPendingUpdate = false;
                });

                return rv;
            },
        }
    },
    Catalogue: function() {
        return {
            queue: undefined,

            _childrenDidInit: false,
            _isPendingUpdate: false,

            // Items should push into this array on init()
            items: [],
            // Basket should attach to this prop on init()
            basket: undefined,

            // No point to auto-evaluate init() without required arguments,
            // so must be called explicitly with x-init
            init_: function(queue) {
                this.$nextTick(() => {
                    this.queue = queue;

                    this.$watch('_childrenDidInit', _ => this.process());
                });
            },
            hydrate: async function(data, triggeredByBasket) {
                if (_.isEmpty(data)) return;

                this.items.forEach(item => {
                    for (const skuId of item.sellableSkuIds) {
                        for (const line of data.lines) {
                            if (line.sku_id == skuId) {
                                let isPendingUpdate = false;
                                if (triggeredByBasket) isPendingUpdate = item.setIsPendingUpdate(true);

                                item.skuQuantities[skuId] = line.quantity;
                                item.skuAmounts[skuId] = line.amount;
                                item.skuLabels[skuId] = line.labels.filter(label => label.type == 'pricing');

                                if (triggeredByBasket) item.setIsPendingUpdate(isPendingUpdate);
                                // Don't be tempted to break both loops, we
                                // need to ensure UoM SKUs are also updated!
                                break;
                            }

                            // Items that are not in the current view are ignored
                        }
                    }
                });
            },
            isPendingUpdate: function() {
                return this._isPendingUpdate;
            },
            process: function() {},
        }
    },
    // Unlike BasketItem, a CatalogueItem can represent one or more SKUs (a
    // base SKU, plus UoMs), and attributes comprise a map of sku_id to value
    CatalogueItem: function(
        baseSkuId,
        sellableSkuIds,           // [sku_id, …], includes baseSkuId if applicable
        skuQuantityLimits,        // {sku_id: {min: 0, max: 0}}
        skuQuantities,            // {sku_id: quantity}
        skuAmounts,               // {sku_id: amount}
        skuLabels,                // {sku_id: [label, …]}
        skuQuantitiesInStock,     // {sku_id: {available_quantity: 0, committed_quantity: 0}}
        allowOutOfStockQuantity,
    ) {
        return {
            catalogue: undefined,

            baseSkuId: String(baseSkuId),
            sellableSkuIds: sellableSkuIds || [],
            selectedSkuId: undefined,

            _skuQuantities: skuQuantities || {},
            _skuAmounts: skuAmounts || {},
            _skuLabels: skuLabels || {},

            _skuQuantityLimits: skuQuantityLimits || {},
            _skuQuantitiesInStock: skuQuantitiesInStock || {},

            skuQuantities: {},
            skuAmounts: {},
            skuLabels: {},

            skuQuantityLimits: {},
            skuQuantitiesInStock: {},
            allowOutOfStockQuantity: !(allowOutOfStockQuantity === false),

            _deferUpdates: false,
            _didInit: false,
            _hasFlash: false,
            _hasMultipleSkusInBasket: false,
            _isPendingUpdate: false,
            _nextUpdateTriggeredByBasket: false,

            // No point to auto-evaluate init() without required arguments,
            // so must be called explicitly with x-init
            init_: function(catalogue) {
                // Using a Proxy ensures that we can always return a value
                // (similar behaviour to a defaultdict), which in turn means
                // the UI doesn't freak out when swapping values
                this.skuQuantities = new Proxy({}, {
                    get: (target, name) => name in target ? target[name] : 0,
                    set: (target, prop, value) => {
                        // Implement our watcher directly rather than use
                        // $watch to avoid issues with Proxying a Proxy (also
                        // means we have direct access to the affected key)
                        if (target[prop] == value) return true;

                        target[prop] = value;
                        if (this._didInit && !this._isPendingUpdate) this.update(prop, value);
                        return true;
                    },
                });
                this.skuQuantityLimits = new Proxy({}, {
                    get: (target, name) => name in target ? target[name] : {'min': 0, 'max': null},
                });
                this.skuAmounts = new Proxy({}, {
                    get: (target, name) => name in target ? target[name] : 0,
                });
                this.skuLabels = new Proxy({}, {
                    get: (target, name) => name in target ? target[name] : [],
                });
                this.skuQuantitiesInStock = new Proxy({}, {
                    get: (target, name) => name in target ? target[name] : {'available': 0, 'committed': 0},
                });

                // Ensure object consistency and defaults
                this.sellableSkuIds.forEach(skuId => {
                    this.skuQuantities[skuId] = this._skuQuantities[skuId] || 0;
                    this.skuAmounts[skuId] = this._skuAmounts[skuId] || 0;
                    this.skuLabels[skuId] = this._skuLabels[skuId] || [];

                    this.skuLabels[skuId] = this.skuLabels[skuId].filter(label => label.type == 'pricing');

                    this.skuQuantityLimits[skuId] = this._skuQuantityLimits[skuId] || {min: 0, max: null};

                    if (this._skuQuantitiesInStock[skuId]) {
                        this.skuQuantitiesInStock[skuId] = {'available': this._skuQuantitiesInStock[skuId].available_quantity, 'committed': this._skuQuantitiesInStock[skuId].committed_quantity};
                    } else {
                        this.skuQuantitiesInStock[skuId] = {'available': 0, 'committed': 0};
                    }
                });

                this.$nextTick(() => {
                    this.catalogue = catalogue;
                    this.catalogue.items.push(this);

                    const urlParams = new URLSearchParams(window.location.search);
                    const filteredSkuId = String(urlParams.get('sku_id'));
                    if (this.sellableSkuIds.includes(filteredSkuId)) this.selectedSkuId = filteredSkuId;

                    this.selectedSkuId = this.selectedSkuId || (this.sellableSkuIds.includes(this.baseSkuId) ? this.baseSkuId : this.sellableSkuIds[0]);

                    this.$watch('selectedSkuId', () => this.updateSelectedSkuId());
                    this.updateSelectedSkuId();

                    this.$dispatch('item-inited');
                    this._didInit = true;
                });
            },
            hasFlash: function() {
                return this._hasFlash;
            },
            setFlash: function(text, classes, timeout=1000) {
                if (!this.$refs.flash) return;

                this.$refs.flash.innerHTML = text;
                this.$refs.flash.classList.add(classes);
                this._hasFlash = true;

                setTimeout(() => {
                    this._hasFlash = false;
                    this.$refs.flash.classList.remove(classes);
                }, timeout);
            },
            hasMultipleSkusInBasket: function() {
                return this._hasMultipleSkusInBasket;
            },
            isPendingUpdate: function() {
                return this._isPendingUpdate;
            },
            setIsPendingUpdate: function(value) {
                // Returns the current value, which can be restored later
                const isPendingUpdate = this._isPendingUpdate;
                this._isPendingUpdate = (value === true);
                return isPendingUpdate;
            },
            showInStockBadge: function() {
                const availableStock = this.skuQuantitiesInStock[this.selectedSkuId]?.available;
                return availableStock && availableStock >= this.skuQuantities[this.selectedSkuId];
            },
            showOutOfStockBadge: function() {
                const availableStock = this.skuQuantitiesInStock[this.selectedSkuId]?.available;
                return !availableStock && !this.allowOutOfStockQuantity;
            },
            showOutOfStockEtaSoonBadge: function() {
                const availableStock = this.skuQuantitiesInStock[this.selectedSkuId]?.available;
                return !availableStock && this.allowOutOfStockQuantity;
            },
            showStockQuantityExceededLabel: function() {
                const availableStock = this.skuQuantitiesInStock[this.selectedSkuId]?.available;
                return availableStock < this.skuQuantities[this.selectedSkuId];
            },
            update: function(skuId, quantity) {
                if (this._deferUpdates) return;

                this._isPendingUpdate = true;
                this.catalogue._isPendingUpdate = true;
                this.catalogue.basket._isPendingUpdate = true;

                let triggeredByCatalogue = true;
                if (this._nextUpdateTriggeredByBasket) {
                    triggeredByCatalogue = false;
                    this._nextUpdateTriggeredByBasket = false;
                }

                let rv = this.catalogue.queue.push({
                    resource: new URL(`/${config.instance}/orders/${this.catalogue.basket.orderId}/update`, window.location.href),
                    options: {
                        method: 'PATCH',
                        mode: 'same-origin',
                        credentials: 'same-origin',
                        body: JSON.stringify({
                            lines: [{
                                sku_id: skuId,
                                quantity: quantity,
                            }],
                        }),
                        headers: {
                            'Content-Type': 'application/json',
                            'Accept': 'application/json',
                            'X-CSRFToken': config.csrfToken,
                        },
                    }
                })
                .then(r => {
                    if (!r.ok) throw new Error('Invalid response', {response: r});
                    return r.json();
                })
                .then(data => {
                    this.catalogue.hydrate(data)
                    .then(() => {
                        // Force removal of items reduced to zero-quantity
                        this.catalogue.basket.hydrate(data, triggeredByCatalogue)
                    })
                    .then(() => {
                        this._isPendingUpdate = false;
                        this.updateSelectedSkuId();

                        this.catalogue._isPendingUpdate = false;
                        this.catalogue.basket._isPendingUpdate = false;
                    });
                })
                .catch(e => {
                    console.warn(e);
                    // TODO: show fetch errors (see toggleErrorState()) when
                    // Basket is closed; use flash?

                    this._isPendingUpdate = false;
                    this.updateSelectedSkuId();

                    this.catalogue._isPendingUpdate = false;
                    this.catalogue.basket._isPendingUpdate = false;
                });

                return rv;
            },
            updateSelectedSkuId: function() {
                const skuChannelMax = this.skuQuantityLimits[this.selectedSkuId]?.max || null;

                // Calculate inventory constraint
                const effectiveMaxQuantity = Math.max(
                    Math.min(
                        // Ensure any non-zero stock quantity isn't floored
                        // by a null or zero skuChannelMax
                        skuChannelMax || 10000,
                        this.skuQuantitiesInStock[this.selectedSkuId]?.available || 0,
                    ),
                    0,
                );

                // Set NumberSpinner constraints
                const numberSpinner = $component(`catalogue-item-spinner-${this.baseSkuId}`);
                numberSpinner.updateState(
                    null,
                    this.skuQuantityLimits[this.selectedSkuId]?.min || 0,
                    // Max uses null, not zero, as "no limit"
                    this.allowOutOfStockQuantity ? skuChannelMax : effectiveMaxQuantity,
                );

                // Not strictly a side effect of selectedSkuId but…
                this._hasMultipleSkusInBasket = Object.values(this.skuQuantities).filter(n => n > 0).length > 1;
            },
            reset: function(skuId, triggeredByBasket) {
                if (triggeredByBasket) this._nextUpdateTriggeredByBasket = true;

                this.skuQuantities[skuId] = 0;
                this.skuAmounts[skuId] = 0;
            },
            resetAll: function(triggeredByBasket) {
                if (triggeredByBasket) this._nextUpdateTriggeredByBasket = true;

                sellableSkuIds.forEach(skuId => {
                    this.skuQuantities[skuId] = 0;
                    this.skuAmounts[skuId] = 0;
                });
            },
        }
    },
};

//# sourceMappingURL=app.bundle.js.map
